import type { ScreenRect } from '../state/itemReveal';

/**
 * The editor panels that float over the stage. Floating chrome keeps clear of them, and
 * placement treats what is left as the area the user can actually see. The chat column is not
 * one of them: it stands beside the stage, which narrows for it.
 */
export const EDITOR_CHROME = '.topbar .top-bar, .topbar .top-side > *, .agent-status, .rail, .wall-mode, .bottom-left, .zoom-menu, .history-pill, .catalog-pill, .help-menu, .stairs-buttons, .item-inspector, .catalog-panel, .wall-tools, .panel, .model-loading-status, .editor-notices > .banner, .participant-popup';

/** The panels on the stage's right that reach under the header, as `noticesInset` meets them. */
export const NOTICES_NEIGHBOURS = ':scope > :is(.item-inspector, .catalog-panel, .panel):not(.is-closing)';
/** The column under the header as app.css sets `.editor-notices`: its widest, and its margin to the edges of its band. */
const NOTICES_WIDTH = 580, NOTICES_MARGIN = 14;
/** The narrowest the column gets beside a panel; with less room it stays in the middle, as the panel leaves too little. */
const NOTICES_MIN_WIDTH = 280;

/**
 * What the column under the header (the wall-thickness bar, the notices and the models' loading status) leaves of the
 * stage on the right, in px. It hangs in the middle of the stage, under the header; a panel on the right that would come
 * within its margin of it — the inspector, the catalog, the lighting — moves it into the middle of what the panel leaves,
 * so that neither covers the other. `panelLefts` are those panels' left edges, from the stage's left.
 */
export function noticesInset(stageWidth: number, panelLefts: readonly number[]): number {
  const edge = Math.min(stageWidth, ...panelLefts);
  const width = Math.min(NOTICES_WIDTH, stageWidth - 2 * NOTICES_MARGIN);
  const reaches = (stageWidth + width) / 2 + NOTICES_MARGIN > edge;
  return reaches && edge - 2 * NOTICES_MARGIN >= NOTICES_MIN_WIDTH ? stageWidth - edge : 0;
}

/**
 * Anything under 12px in either direction is not a panel: hidden controls and the
 * screen-reader-only loading pill collapse to a clipped 1×1 box that nothing should
 * be pushed away from.
 */
export function chromeRects(root: ParentNode = document, exclude?: string): ScreenRect[] {
  const skip = exclude ? new Set(root.querySelectorAll(exclude)) : null;
  return [...root.querySelectorAll(EDITOR_CHROME)]
    .filter(element => !skip?.has(element))
    .map(element => {
      const rect = element.getBoundingClientRect();
      return { x: rect.left, y: rect.top, width: rect.width, height: rect.height };
    })
    .filter(rect => rect.width >= 12 && rect.height >= 12);
}
